// Toast queue — imperative transient notifications. See docs/framework/ui/toasts.md.

import { readonly, shallowRef, type Ref } from 'vue'

export type ToastKind = 'info' | 'success' | 'warn' | 'error'

export interface ToastAction {
  label: string
  run:   () => void | Promise<void>
}

export interface ToastOptions {
  /** Milliseconds until auto-dismiss. null = sticky. Kind default applies if omitted. */
  timeoutMs?: number | null
  /** Replace any existing toast with the same key. Use for bursty events. */
  dedupeKey?: string
  /** Optional in-toast button. Runs `run`, then dismisses (unless run throws). */
  action?:    ToastAction
}

export interface ToastEntry {
  id:         number
  kind:       ToastKind
  message:    string
  timeoutMs:  number | null
  dedupeKey:  string | null
  action:     ToastAction | null
}

/** Kind-specific default timeout. Errors are sticky — the user acknowledges them. */
const DEFAULT_TIMEOUT: Record<ToastKind, number | null> = {
  info:    4000,
  success: 4000,
  warn:    6000,
  error:   null,
}

// shallowRef because entries hold caller-supplied action closures we don't
// want proxied. The queue is always replaced immutably, never mutated in place.
const toasts = shallowRef<ToastEntry[]>([])
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextId = 1

function push(kind: ToastKind, message: string, options: ToastOptions = {}): number {
  if (options.dedupeKey) {
    const existing = toasts.value.find((t) => t.dedupeKey === options.dedupeKey)
    if (existing) dismiss(existing.id)
  }

  const id = nextId++
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT[kind]

  toasts.value = [
    ...toasts.value,
    {
      id,
      kind,
      message,
      timeoutMs,
      dedupeKey: options.dedupeKey ?? null,
      action:    options.action    ?? null,
    },
  ]

  if (timeoutMs != null && timeoutMs > 0) {
    timers.set(id, setTimeout(() => dismiss(id), timeoutMs))
  }

  return id
}

/** Dismiss a toast by id. Idempotent. */
export function dismiss(id: number): void {
  const t = timers.get(id)
  if (t) {
    clearTimeout(t)
    timers.delete(id)
  }
  toasts.value = toasts.value.filter((e) => e.id !== id)
}

/** Dismiss all currently visible toasts and cancel all pending timers. */
export function dismissAll(): void {
  for (const t of timers.values()) clearTimeout(t)
  timers.clear()
  toasts.value = []
}

export const toast = {
  info:    (message: string, options?: ToastOptions) => push('info',    message, options),
  success: (message: string, options?: ToastOptions) => push('success', message, options),
  warn:    (message: string, options?: ToastOptions) => push('warn',    message, options),
  error:   (message: string, options?: ToastOptions) => push('error',   message, options),
  /** Escape hatch for the rare case where kind is decided at call time. */
  push:    (opts: ToastOptions & { kind: ToastKind; message: string }): number =>
             push(opts.kind, opts.message, opts),
  dismiss,
  dismissAll,
}

/** Read the current queue. Consumed by ToastLayer; leaf code uses `toast.*`. */
export function useToasts(): { toasts: Readonly<Ref<readonly ToastEntry[]>> } {
  return { toasts: readonly(toasts) as Readonly<Ref<readonly ToastEntry[]>> }
}
